import { useEffect, useState } from "react";
import { Printer, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, fmt } from "@/lib/api";
import { downloadWith } from "@/lib/portalApi";
import { Section, Field } from "@/components/common";

const today = () => new Date().toISOString().slice(0, 10);

function Period({ f, setF, path, name, testId }) {
  const q = new URLSearchParams(Object.entries({ ...f, format: "pdf" }).filter(([, v]) => v)).toString();
  return (
    <div className="flex flex-wrap items-end gap-3 border-b border-slate-100 p-5 print:hidden">
      <Field label="From"><Input type="date" data-testid={`${testId}-from`} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></Field>
      <Field label="To"><Input type="date" data-testid={`${testId}-to`} value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></Field>
      <Button variant="outline" size="sm" data-testid={`${testId}-print`} onClick={() => window.print()}><Printer className="mr-1 h-4 w-4" />Print</Button>
      <Button variant="outline" size="sm" data-testid={`${testId}-pdf`} onClick={() => downloadWith(api, `${path}?${q}`, `${name}.pdf`, true)}><FileDown className="mr-1 h-4 w-4" />PDF</Button>
    </div>
  );
}

function useFin(path, f) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(path, { params: f }).then((r) => setD(r.data)).catch(() => setD(null)); }, [path, f.date_from, f.date_to]); // eslint-disable-line react-hooks/exhaustive-deps
  return d;
}

export function ProfitLossReport({ cur }) {
  const [f, setF] = useState({ date_from: `${today().slice(0, 8)}01`, date_to: today() });
  const d = useFin("/reports/profit-loss", f);
  const rows = (list) => list.map((r) => <tr key={r.code}><td className="font-mono text-xs">{r.code}</td><td>{r.name}</td><td className="po-num">{fmt(r.amount, cur)}</td></tr>);
  return (
    <Section title="Profit and loss" testId="pnl-report">
      <Period f={f} setF={setF} path="/reports/profit-loss" name="profit-and-loss" testId="pnl" />
      {d && <div className="overflow-x-auto"><table className="po-table" data-testid="pnl-table"><tbody>
        <tr className="po-subhead"><td colSpan={3}>Income</td></tr>{rows(d.income)}<tr className="po-total"><td colSpan={2}>Total income</td><td className="po-num">{fmt(d.total_income, cur)}</td></tr>
        <tr className="po-subhead"><td colSpan={3}>Expenses</td></tr>{rows(d.expenses)}<tr className="po-total"><td colSpan={2}>Total expenses</td><td className="po-num">{fmt(d.total_expenses, cur)}</td></tr>
        <tr className="po-total text-base"><td colSpan={2}>{d.net_profit >= 0 ? "Net profit" : "Net loss"}</td><td className="po-num" data-testid="pnl-net">{fmt(d.net_profit, cur)}</td></tr>
      </tbody></table></div>}
    </Section>
  );
}

export function TrialBalanceReport({ cur }) {
  const [f, setF] = useState({ date_from: "", date_to: today() });
  const d = useFin("/reports/trial-balance", f);
  return (
    <Section title="Trial balance" testId="tb-report">
      <Period f={f} setF={setF} path="/reports/trial-balance" name="trial-balance" testId="tb" />
      {d && <div className="overflow-x-auto"><table className="po-table" data-testid="tb-table">
        <thead><tr><th>Code</th><th>Account</th><th>Type</th><th className="po-num">Debit</th><th className="po-num">Credit</th></tr></thead>
        <tbody>{d.rows.map((r) => <tr key={r.code}><td className="font-mono text-xs">{r.code}</td><td>{r.name}</td><td className="capitalize">{r.type}</td><td className="po-num">{r.debit ? fmt(r.debit, cur) : ""}</td><td className="po-num">{r.credit ? fmt(r.credit, cur) : ""}</td></tr>)}
          <tr className="po-total"><td colSpan={3}>Total</td><td className="po-num" data-testid="tb-total-debit">{fmt(d.total_debit, cur)}</td><td className="po-num" data-testid="tb-total-credit">{fmt(d.total_credit, cur)}</td></tr></tbody></table></div>}
    </Section>
  );
}
